import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { HistoryWrapper, EmptyHistory, ReferStatusHistory } from './styles';
import { InstaremAvatar } from '@Components';
import { ReferHistoryProps, History } from '../interface';
import { REFERRAL_SMALL_ICON, ALERT_BELL_ICON, BW_WALLET_ICON } from '@Images';
import { INSTAPOINTS_URL } from '../../../utils/constants';
import { selectUserStatusReducer } from '@Redux/selectors';
import { logEventV2 } from '@Utils/events/logUserEvents';
import {
  EVENT_ROUTE_KEYS,
  EventItemSection,
  EventNamesV2,
  FLOW_TYPE
} from '@Utils/events/eventsConstants';

const ReferHistory: React.FC<ReferHistoryProps> = ({
  historyData,
  labels,
  url,
  isUniformReferralCountry = false
}): React.ReactElement => {
  const isEmptyHistory = historyData?.length === 0;
  const referralActivity = labels?.referralActivitySection;
  const sendReminderActivity = referralActivity?.shareMessage;
  const sendReminderSubject = sendReminderActivity?.emailSubject;
  const sendReminderMessage = sendReminderActivity?.message;
  const userStatus = useSelector(selectUserStatusReducer);
  const fullName = userStatus?.remitter_individual_full_name;
  const sourceCurrency = userStatus?.source_currency;

  const logEvent = (historyItem: History, index: number) => {
    const eventProperty = {
      section: EventItemSection.TABS,
      item: historyItem?.status,
      position: index + 1,
      pageName: EVENT_ROUTE_KEYS.ReferralHistory,
      flowType: FLOW_TYPE.REFERRALS
    };
    logEventV2(EventNamesV2.ITEM_CLICKED, eventProperty);
  };

  const renderBonusStatus = (historyItem: History) => {
    const { referral_bonus_amount, referral_bonus_redemption_status } = historyItem;
    const bonusLabel = `${referral_bonus_amount} ${sourceCurrency} bonus`;

    if (referral_bonus_redemption_status === 'AVAILABLE') {
      return (
        <Link to={INSTAPOINTS_URL} className="sendReminderContainer">
          <img src={ALERT_BELL_ICON} alt="Bell Icon" className="alertBellIcon" />
          <span className="sendReminderText">{bonusLabel} available to redeem</span>
        </Link>
      );
    }

    const statusLabel =
      referral_bonus_redemption_status === 'REDEEMED'
        ? `${bonusLabel} redeemed`
        : `${bonusLabel} expired`;

    return (
      <div className="bonusStatusContainer">
        <img src={BW_WALLET_ICON} alt="Wallet Icon" className="alertBellIcon" />
        <span className="bonusStatusText">{statusLabel}</span>
      </div>
    );
  };

  const renderCompletedAction = (historyItem: History) => {
    if (isUniformReferralCountry) {
      return renderBonusStatus(historyItem);
    }

    return (
      <Link
        to={INSTAPOINTS_URL}
        className="sendReminderContainer"
        style={{ textDecoration: 'none' }}
      >
        <span className="sendReminderText">Redeem now →</span>
      </Link>
    );
  };

  const renderHistoryItems = () => {
    return historyData.map((historyItem: History, index: number) => (
      <div className="index" key={index}>
        <div className="activityCard">
          <InstaremAvatar
            name={historyItem?.name}
            flagCode={historyItem?.referee_country_code}
          ></InstaremAvatar>
          <div className="statusContainer">
            <div className="referDetails">
              <span className="currenStatus">{historyItem?.name}</span>
              <div
                className={`statusBox ${historyItem?.status === 'Completed' ? 'completed' : ''}`}
              >
                {historyItem?.status}
              </div>
            </div>
            <span
              className={`status ${
                historyItem?.status === 'Completed' ? 'completedDescription' : ''
              }`}
            >
              {historyItem?.status_description}
            </span>
            {historyItem?.status === 'Completed' ? (
              renderCompletedAction(historyItem)
            ) : (
              <a
                href={`mailto:?subject=${sendReminderSubject}
                &body=${sendReminderMessage
                  .replace('{REFEREES_NAME}', historyItem?.name)
                  .replace('{REMITTER_NAME}', fullName)
                  .replace(/\n/g, '%0D%0A')}
                      `}
                id="email-link"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logEvent(historyItem, index)}
                className="sendReminderContainer"
              >
                <img src={ALERT_BELL_ICON} alt="Alert Bell Icon" className="alertBellIcon" />
                <span className="sendReminderText">{referralActivity?.sendReminder}</span>
              </a>
            )}
          </div>
        </div>
        {index !== historyData.length - 1 && <div className="separationLine"></div>}
      </div>
    ));
  };

  return (
    <HistoryWrapper>
      {isEmptyHistory ? (
        <EmptyHistory>
          <div className="zeroReferBox">
            <div>
              <img alt="" src={REFERRAL_SMALL_ICON} />
            </div>
            <span className="emptyStaticText">{referralActivity?.noReferralFound}</span>
          </div>
        </EmptyHistory>
      ) : (
        <ReferStatusHistory>{renderHistoryItems()}</ReferStatusHistory>
      )}
    </HistoryWrapper>
  );
};

export default ReferHistory;
